import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { Email, Language, Phone, Place } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import emailjs from "emailjs-com";
import { motion, useReducedMotion } from "framer-motion";
import { ChangeEvent, FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SectionHeading from "../Shared/SectionHeading";

interface FormData {
  from_name: string;
  email: string;
  phone: string;
  message: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "warning" | "info";
}

const infoItems = [
  {
    icon: <Email sx={{ color: "#d85f57", fontSize: 22 }} />,
    label: "Email",
    content: (
      <Link href="mailto:contact@vistafusionstudio.com" underline="none">
        contact@vistafusionstudio.com
      </Link>
    ),
  },
  {
    icon: <Phone sx={{ color: "#4f9f5f", fontSize: 22 }} />,
    label: "Call Us",
    content: <Typography>+91 90303 56464</Typography>,
  },
  {
    icon: <Language sx={{ color: "#3b90db", fontSize: 22 }} />,
    label: "Website",
    content: (
      <Link
        href="https://www.vistafusionstudio.com"
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
      >
        www.vistafusionstudio.com
      </Link>
    ),
  },
  {
    icon: <Place sx={{ color: "#e07a47", fontSize: 22 }} />,
    label: "Studio Address",
    content: (
      <Typography>
        Praneeth Pranav Knightwoods, 437, Beeramguda
        <br />
        Hyderabad, Telangana 502032
      </Typography>
    ),
  },
];

const ContactUs = () => {
  const defaultFormData: FormData = {
    from_name: "",
    email: "",
    phone: "",
    message: "",
  };

  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [isSendBtnDisabled, setIsSendBtnDisabled] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const isContactUsPage = location.pathname === "/contact-us";
  const prefersReducedMotion = useReducedMotion();

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isValidEmail = (email: string): boolean =>
    /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);

  const sendEmail = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      !formData.from_name ||
      !formData.email ||
      !isValidEmail(formData.email) ||
      !formData.message
    ) {
      setSnackbar({
        open: true,
        message: "Please fill out all required fields correctly.",
        severity: "error",
      });
      return;
    }

    setIsSendBtnDisabled(true);
    emailjs
      .send(
        "service_ez66qzn",
        "template_a4hk5m4",
        { ...formData, service_type: "Residence" } as any,
        "R9Yl9lyT79sQieGAV",
      )
      .then(() => {
        setSnackbar({
          open: true,
          message: "Message sent successfully!",
          severity: "success",
        });
        setFormData(defaultFormData);
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "Failed to send message. Please try again.",
          severity: "error",
        });
      })
      .finally(() => setIsSendBtnDisabled(false));
  };

  return (
    <Box
      id="contact-us-section"
      sx={{
        px: { xs: 0, md: 1.2 },
        py: { xs: 4.6, md: 5.8 },
        background: isContactUsPage
          ? "linear-gradient(180deg, #f8f3ee 0%, #fcfaf7 100%)"
          : "transparent",
      }}
    >
      <Container maxWidth="xl">
        <SectionHeading
          eyebrow="Get In Touch"
          title="Let’s Design Your Space Together"
          subtitle="Share your requirements and we’ll connect with a personalized consultation plan, timeline estimate, and scope direction."
          titleAs={isContactUsPage ? "h1" : "h2"}
        />

        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30, scale: 0.985 }}
          whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={
            prefersReducedMotion
              ? undefined
              : { duration: 0.64, ease: [0.22, 1, 0.36, 1] }
          }
        >
          <Box
            sx={{
              borderRadius: { xs: "22px", md: "30px" },
              p: { xs: 1.4, sm: 1.8, md: 2.4 },
              background:
                "linear-gradient(152deg, rgba(255,255,255,0.86), rgba(255,247,243,0.72))",
              border: "1px solid rgba(128,51,65,0.13)",
              boxShadow: "0 16px 38px rgba(20,8,10,0.1)",
            }}
          >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 2, md: 2.4 },
            }}
          >
            <Box
              sx={{
                borderRadius: "18px",
                border: "1px solid rgba(128,51,65,0.14)",
                overflow: "hidden",
                minHeight: { xs: 260, md: 420 },
              }}
            >
              <Box
                component="iframe"
                title="Vista Fusion Studio map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3804.7697215070184!2d78.30128627555037!3d17.518502699013943!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb8d8fc51310d7%3A0x161cba0b8f3f3265!2sVista%20Fusion%20Studio!5e0!3m2!1sen!2sin!4v1732974989465!5m2!1sen!2sin"
                sx={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
              />
            </Box>

            <Box
              sx={{
                borderRadius: "18px",
                border: "1px solid rgba(128,51,65,0.14)",
                background: "rgba(255,255,255,0.68)",
                p: { xs: 1.5, sm: 1.8, md: 2.1 },
              }}
            >
              <Stack spacing={1.2}>
                {infoItems.map((item) => (
                  <motion.div
                    key={item.label}
                    initial={prefersReducedMotion ? false : { opacity: 0, x: 16 }}
                    whileInView={prefersReducedMotion ? undefined : { opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={
                      prefersReducedMotion
                        ? undefined
                        : { duration: 0.44, ease: [0.22, 1, 0.36, 1] }
                    }
                  >
                    <Box
                      sx={{
                        borderRadius: "12px",
                        p: 1.1,
                        border: "1px solid rgba(128,51,65,0.12)",
                        background: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
                        {item.icon}
                        <Typography
                          sx={{
                            fontFamily: '"Manrope", sans-serif',
                            fontWeight: 700,
                            color: "#3a1b22",
                            fontSize: "0.92rem",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.55,
                          color: "rgba(58,27,34,0.82)",
                          "& a": {
                            color: "rgba(58,27,34,0.82)",
                          },
                          "& p": {
                            fontFamily: '"Manrope", sans-serif',
                            fontSize: "0.88rem",
                            lineHeight: 1.62,
                          },
                        }}
                      >
                        {item.content}
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Stack>
            </Box>
          </Box>

          <Box
            component="form"
            onSubmit={sendEmail}
            sx={{
              mt: 2.1,
              borderRadius: "18px",
              border: "1px solid rgba(128,51,65,0.14)",
              background: "rgba(255,255,255,0.68)",
              p: { xs: 1.4, sm: 1.8, md: 2.2 },
            }}
          >
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                  gap: 1.3,
                }}
              >
                <TextField
                  fullWidth
                  label="Name"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Tell us about your project"
                name="message"
                value={formData.message}
                onChange={handleChange}
                variant="outlined"
              />

              <Box sx={{ display: "flex", justifyContent: "center", pt: 0.2 }}>
                <Button
                  type="submit"
                  disabled={isSendBtnDisabled}
                  endIcon={<ArrowOutwardRoundedIcon />}
                  sx={{
                    px: 2.7,
                    py: 1.06,
                    borderRadius: "999px",
                    textTransform: "none",
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 700,
                    color: "#fff",
                    background:
                      "linear-gradient(137deg, #803341 0%, #a85156 100%)",
                    boxShadow: "0 10px 18px rgba(128,51,65,0.28)",
                    "&:hover": {
                      background:
                        "linear-gradient(137deg, #6f2c39 0%, #954349 100%)",
                    },
                  }}
                >
                  {isSendBtnDisabled ? "Sending..." : "Send Inquiry"}
                </Button>
              </Box>
            </Stack>
          </Box>
          </Box>
        </motion.div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {isContactUsPage ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4.4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/")}
              sx={{
                px: 2.6,
                py: 1.02,
                borderRadius: "999px",
                textTransform: "none",
                borderColor: "rgba(128,51,65,0.45)",
                color: "#672936",
                fontFamily: '"Manrope", sans-serif',
                fontWeight: 700,
                "&:hover": {
                  borderColor: "#803341",
                  backgroundColor: "rgba(128,51,65,0.07)",
                },
              }}
            >
              Back to Home
            </Button>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default ContactUs;
